import { availabilitiesAtom } from "@/atoms";
import { minuteNowAtom, tenantTimezoneAtom } from "@/atoms/tenant-clock";
import {
  indoorCategoriesAtom,
  indoorCategoriesItemOffersAtom,
  indoorItemOffersAtom,
} from "@/atoms/indoor";
import { isOfferVisibleNow } from "@/lib/catalog-visibility";
import { makeItemOfferDetailAtoms } from "@/atoms/make-item-offer-detail-atoms";
import { ItemDetailSkeleton } from "@/components/item/ItemDetailSkeleton";
import { ItemHero } from "@/components/item/ItemHero";
import { ItemStickyTitleBar } from "@/components/item/ItemStickyTitleBar";
import { ItemTitleBlock } from "@/components/item/ItemTitleBlock";
import { ItemUnavailableState } from "@/components/item/ItemUnavailableState";
import { ImageModal } from "@/components/modals";
import { recordStorefrontEvent } from "@/domain/storefront-analytics";
import { useAtomValue } from "jotai";
import { useEffect, useMemo, useRef, useState } from "react";
import { ItemOfferCard } from "@/components/home";
import { OptionGroupView } from "./option-group-view";
import { Divider, PageContainer, SectionHeader, StaggerGroup, StaggerItem, Text } from "@/components/ui";

interface MenuItemModalContentProps {
  onClose: () => void;
  itemOfferId: string;
  handleItemClick: (itemId: string) => void;
  contentRef?: React.RefObject<HTMLDivElement>;
}

/**
 * Indoor info-only product modal. Same visual structure as ItemModalContent
 * (image, title, price, description, options) — no add-to-bag CTA and no
 * quantity/cart atoms.
 */
export function MenuItemModalContent({
  onClose,
  itemOfferId,
  handleItemClick,
  contentRef,
}: MenuItemModalContentProps) {
  const { categoryAtom, itemOfferAtom, itemOfferOptionGroupsAtom, itemAtom, minimumOfferPriceAtom } =
    useMemo(
      () =>
        makeItemOfferDetailAtoms(itemOfferId, {
          categoriesAtom: indoorCategoriesAtom,
          itemOffersAtom: indoorItemOffersAtom,
        }),
      [itemOfferId],
    );

  const category = useAtomValue(categoryAtom);
  const itemOffer = useAtomValue(itemOfferAtom);
  const optionGroups = useAtomValue(itemOfferOptionGroupsAtom);
  const item = useAtomValue(itemAtom);
  const minimumOfferPrice = useAtomValue(minimumOfferPriceAtom);
  const categoriesItemOffers = useAtomValue(indoorCategoriesItemOffersAtom);
  const availabilities = useAtomValue(availabilitiesAtom);
  const timeZone = useAtomValue(tenantTimezoneAtom);
  const now = useAtomValue(minuteNowAtom)

  const similarItemOffers = useMemo(() => {
    if (!category) return [];
    const sameCategory = categoriesItemOffers[category.id] ?? [];
    return sameCategory.filter((io) => io.id !== itemOfferId);
  }, [categoriesItemOffers, category, itemOfferId]);

  const viewFired = useRef(false);
  useEffect(() => {
    if (viewFired.current || !item) return;
    viewFired.current = true;
    recordStorefrontEvent({ type: "item_view", itemId: item.id, itemName: item.name });
  }, [item]);

  const [isImageVisible, setIsImageVisible] = useState(true);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const imageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsImageVisible(entry.intersectionRatio > 0.1);
      },
      {
        threshold: [0, 0.1, 0.5, 1],
        rootMargin: "-50px 0px -50px 0px",
      },
    );

    const imageElement = imageRef.current;
    if (imageElement) observer.observe(imageElement);

    return () => {
      if (imageElement) observer.unobserve(imageElement);
    };
  }, []);

  useEffect(() => {
    contentRef?.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [itemOfferId, contentRef]);

  if (!itemOfferId) {
    return (
      <div className="flex items-center justify-center h-64">
        <Text tone="muted">Item não encontrado</Text>
      </div>
    );
  }

  if (!category || !itemOffer || !item) return <ItemDetailSkeleton />;

  if (!isOfferVisibleNow(itemOffer, availabilities, category, { now, timeZone })) {
    return <ItemUnavailableState onClose={onClose} />;
  }

  const handleImageClick = () => {
    if (item.image?.URL) setIsImageModalOpen(true);
  };

  return (
    <div className="mx-auto max-w-shell pb-12">
      <div className="flex flex-col">
        <ItemHero
          containerRef={imageRef}
          name={item.name}
          imageUrl={item.image?.URL}
          imageCrc={item.image?.["CRC-32"]}
          onZoom={handleImageClick}
          isPromotional={itemOffer.isPromotional || category.isPromotional}
          promotionalText={itemOffer.promotionalBannerText || category.promotionalBannerText}
        />

        <ItemStickyTitleBar title={item.name} onClose={onClose} imageVisible={isImageVisible} />

        <ItemTitleBlock
          key={itemOfferId}
          name={item.name}
          description={item.description}
          itemOffer={itemOffer}
          minimumOfferPrice={minimumOfferPrice}
        >
          {optionGroups.map((optionGroup) => (
            <OptionGroupView key={optionGroup.id} optionGroup={optionGroup} />
          ))}

          {optionGroups.length === 0 && similarItemOffers.length > 0 && (
            <>
              <Divider variant="band" className="mt-10" />
              <section
                key={`similar-${itemOfferId}`}
                className="animate-slide-up-soft bg-gradient-to-b from-page/60 to-card pb-10 pt-8"
                aria-labelledby="see-also-title"
              >
                <PageContainer surface="card">
                  <SectionHeader
                    id="see-also-title"
                    title="Veja também"
                    spacing="lg"
                    description={
                      <>
                        Outros itens em{" "}
                        <Text as="span" weight="semibold">
                          {category.name}
                        </Text>
                      </>
                    }
                  />
                  <StaggerGroup className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {similarItemOffers.map((offer, index) => (
                      <StaggerItem key={offer.id} className="h-full">
                        <ItemOfferCard
                          itemOffer={offer}
                          status="AVAILABLE"
                          disabled={offer.status === "UNAVAILABLE"}
                          index={index}
                          itemOffers={similarItemOffers}
                          handleItemClick={handleItemClick}
                          itemId={offer.id}
                          hideQuantityButton
                        />
                      </StaggerItem>
                    ))}
                  </StaggerGroup>
                </PageContainer>
              </section>
            </>
          )}
        </ItemTitleBlock>
      </div>

      <ImageModal
        isOpen={isImageModalOpen}
        onClose={() => setIsImageModalOpen(false)}
        imageUrl={item.image?.URL}
        crc={item.image?.["CRC-32"]}
        alt={item.name}
      />
    </div>
  );
}
